// Phase 3.2a — Cycle Count list table.
//
// Replaces the MUI <Table> in cycleCount.tsx with the shared shadcn DataTable
// (docs/09 §3.2). Presentation only: row click still calls the screen's
// `redirectHandler`, the completed-row export icon still calls the screen's
// `cycleCountReportData`, and search + server pagination reuse the same handlers
// the screen dispatches today. Status text is rendered via <StatusBadge> so
// Completed maps to the success token (legacy coloured by statusRefId; the same
// three states — completed / in progress / yet to start — map identically here).
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Download } from "lucide-react";
import moment from "moment";
import { DataTable } from "../../shared/data-table";
import { StatusBadge } from "../../shared/status-badge";
import { Button } from "ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "ui/tooltip";

export interface CycleCountTableProps {
  data: any[];
  loading: boolean;
  onRowClick: (record: any) => void;
  onDownload: (record: any) => void;
  reportLoading: boolean;
  searchValue?: string;
  onSearch: (query: string) => void;
  searchPlaceholder?: string;
  title?: React.ReactNode;
  headerActions?: React.ReactNode;
  pagination: { page: number; total: number; onChange: (page: number) => void };
}

export function CycleCountTable({
  data,
  loading,
  onRowClick,
  onDownload,
  reportLoading,
  searchValue,
  onSearch,
  searchPlaceholder,
  title,
  headerActions,
  pagination,
}: CycleCountTableProps) {
  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        cell: ({ row }) => {
          const record = row.original;
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="block max-w-[220px] truncate font-medium">{record?.name}</span>
                </TooltipTrigger>
                <TooltipContent>{record?.name}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
      {
        id: "cycleCountId",
        header: "Cycle Count ID",
        cell: ({ row }) => {
          const record = row.original;
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="block max-w-[180px] truncate text-muted-foreground">{record?.cycleCountId}</span>
                </TooltipTrigger>
                <TooltipContent>{record?.cycleCountId}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original?.status} />,
      },
      {
        id: "createdBy",
        header: "Created By",
        cell: ({ row }) => {
          const record = row.original;
          return (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate">
                  {record?.createdBy}, {moment.utc(record?.createdOn).local().format("YYYY-MMMM-DD")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {moment.utc(record?.createdOn).local().format("HH:mm A")}
                </div>
              </div>
              {/* Export the completed-cycle report — statusRefId 3 == Completed. */}
              {record?.statusRefId == 3 && (
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Download report"
                  disabled={reportLoading}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownload(record);
                  }}
                >
                  <Download className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportLoading]
  );

  return (
    <DataTable
      columns={columns}
      data={data || []}
      loading={loading}
      searchable
      searchValue={searchValue}
      onSearch={onSearch}
      searchPlaceholder={searchPlaceholder}
      onRowClick={onRowClick}
      title={title}
      headerActions={headerActions}
      pagination={pagination}
    />
  );
}

export default CycleCountTable;
